import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import API_URL from '../../config/api';

type Tab = 'home' | 'rent' | 'maintenance' | 'documents';

/* ─── Helpers ─────────────────────────────────────────────────────── */
const fmt = (n: number) =>
  '£' + Number(n || 0).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtDate = (d: string) => (d ? new Date(d).toLocaleDateString('en-GB') : '—');

function greeting() {
  const h = new Date().getHours();
  if (h < 12) return 'Good morning';
  if (h < 18) return 'Good afternoon';
  return 'Good evening';
}

function nextDueDate(rent: any[], lease: any): string | null {
  // Find the most recent unpaid/pending due entry
  const upcoming = rent.find(r => r.status === 'unpaid' || r.status === 'pending');
  if (upcoming?.due_date) return upcoming.due_date;
  // If all paid, calculate next month from payment_day on lease
  if (!lease) return null;
  const payDay = lease.payment_day || 1;
  const now = new Date();
  const next = new Date(now.getFullYear(), now.getMonth() + 1, payDay);
  return next.toISOString();
}

/* ─── CSS ─────────────────────────────────────────────────────────── */
const CSS = `
@import url('https://fonts.googleapis.com/css2?family=Bricolage+Grotesque:wght@400;500;600;700;800&family=DM+Mono:wght@400;500&display=swap');

.tp-wrap * { box-sizing: border-box; }
.tp-wrap {
  font-family: 'Bricolage Grotesque', system-ui, sans-serif;
  background: #f6f7f9;
  min-height: 100vh;
}

/* ── 3D Banner ── */
.tp-banner {
  background: linear-gradient(160deg, #064e3b 0%, #0d9488 35%, #14b8a6 65%, #0369a1 100%);
  padding: 28px 36px 36px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  box-shadow:
    0 14px 0 rgba(6,78,59,0.22),
    0 24px 48px rgba(13,148,136,0.38),
    inset 0 1px 0 rgba(255,255,255,0.18),
    inset 0 -2px 0 rgba(0,0,0,0.12);
  position: relative;
  overflow: hidden;
}
.tp-banner::before {
  content: '';
  position: absolute;
  top: -50%; right: -10%;
  width: 420px; height: 420px;
  background: radial-gradient(circle, rgba(255,255,255,0.13) 0%, transparent 70%);
  border-radius: 50%;
}
.tp-banner-left {
  display: flex; align-items: center; gap: 16px;
  position: relative; z-index: 1;
}
.tp-banner-icon {
  width: 64px; height: 64px;
  background: rgba(255,255,255,0.2);
  backdrop-filter: blur(12px);
  border-radius: 16px;
  display: flex; align-items: center; justify-content: center;
  font-size: 30px;
  border: 2px solid rgba(255,255,255,0.32);
  box-shadow: 0 4px 14px rgba(0,0,0,0.18), inset 0 1px 0 rgba(255,255,255,0.28);
  flex-shrink: 0;
}
.tp-banner-greeting {
  color: rgba(255,255,255,0.75);
  font-size: 11px; font-weight: 700;
  letter-spacing: 0.07em; text-transform: uppercase;
  margin: 0 0 3px;
}
.tp-banner-name {
  color: #fff; font-size: 24px; font-weight: 800;
  letter-spacing: -0.02em; margin: 0;
  text-shadow: 0 2px 10px rgba(0,0,0,0.2);
}
.tp-banner-address {
  color: rgba(255,255,255,0.65);
  font-size: 12px; font-weight: 600;
  margin: 4px 0 0;
  display: flex; align-items: center; gap: 4px;
}
.tp-banner-right {
  display: flex; align-items: center; gap: 16px;
  position: relative; z-index: 1;
}
.tp-banner-date {
  color: rgba(255,255,255,0.9);
  font-size: 13px; font-weight: 700;
  text-align: right;
}
.tp-logout-btn {
  background: rgba(255,255,255,0.18);
  color: #fff; border: 1.5px solid rgba(255,255,255,0.32);
  border-radius: 10px; padding: 9px 18px;
  font-size: 13px; font-weight: 700; cursor: pointer;
  font-family: inherit;
  transition: background 0.15s;
  backdrop-filter: blur(8px);
}
.tp-logout-btn:hover { background: rgba(255,255,255,0.28); }

/* ── Tab nav ── */
.tp-tabs {
  background: #fff;
  border-bottom: 1.5px solid #eaeef2;
  display: flex;
  padding: 0 28px;
  gap: 2px;
  position: sticky; top: 0; z-index: 10;
  box-shadow: 0 2px 8px rgba(0,0,0,0.05);
}
.tp-tab {
  background: none; border: none;
  padding: 14px 18px;
  font-size: 13px; font-weight: 700;
  cursor: pointer; color: #6b7280;
  border-bottom: 2.5px solid transparent;
  transition: all 0.15s;
  font-family: inherit;
  display: flex; align-items: center; gap: 6px;
}
.tp-tab:hover { color: #0d9488; }
.tp-tab.active { color: #0d9488; border-bottom-color: #0d9488; }
.tp-tab-badge {
  background: #ef4444; color: #fff;
  border-radius: 10px; padding: 1px 6px;
  font-size: 10px; font-weight: 800;
}

/* ── Content ── */
.tp-content {
  max-width: 860px;
  margin: 0 auto;
  padding: 28px 24px 80px;
}

/* ── KPI row ── */
.tp-kpi-row {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(155px, 1fr));
  gap: 14px;
  margin-bottom: 28px;
}
.tp-kpi {
  border-radius: 20px;
  padding: 22px 22px 20px;
  position: relative;
  overflow: hidden;
  cursor: default;
}
.tp-kpi-icon { font-size: 22px; margin-bottom: 10px; display: block; }
.tp-kpi-label {
  font-size: 10px; font-weight: 700;
  text-transform: uppercase; letter-spacing: .08em;
  color: rgba(255,255,255,0.82);
  margin-bottom: 3px;
}
.tp-kpi-val {
  font-size: 24px; font-weight: 800;
  letter-spacing: -.02em; line-height: 1;
  color: #fff;
  margin-bottom: 4px;
  text-shadow: 0 1px 4px rgba(0,0,0,0.18);
}
.tp-kpi-sub { font-size: 11px; color: rgba(255,255,255,0.75); font-weight: 500; }

/* ── Alert banners ── */
.tp-alert {
  border-radius: 12px; padding: 14px 18px;
  display: flex; align-items: flex-start; gap: 12px;
  margin-bottom: 14px; border: 1.5px solid;
}
.tp-alert-icon { font-size: 20px; flex-shrink: 0; margin-top: 1px; }
.tp-alert-title { font-size: 14px; font-weight: 700; }
.tp-alert-sub { font-size: 13px; margin-top: 2px; }

/* ── Cards ── */
.tp-card {
  background: #fff;
  border-radius: 16px;
  border: 1.5px solid #eaeef2;
  padding: 22px 24px;
  margin-bottom: 16px;
}
.tp-card-title { font-size: 15px; font-weight: 800; color: #0f172a; margin: 0 0 18px; }
.tp-grid-2 { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; }
.tp-grid-3 { display: grid; grid-template-columns: repeat(3, 1fr); gap: 16px; }
.tp-field-label {
  font-size: 10px; font-weight: 700; color: #94a3b8;
  text-transform: uppercase; letter-spacing: .06em; margin-bottom: 3px;
}
.tp-field-val { font-size: 14px; font-weight: 600; color: #0f172a; }

/* ── Quick actions ── */
.tp-quick-grid {
  display: grid; grid-template-columns: 1fr 1fr; gap: 12px;
  margin-bottom: 16px;
}
.tp-quick-btn {
  background: #fff; border: 1.5px solid #eaeef2;
  border-radius: 14px; padding: 18px;
  text-align: left; cursor: pointer;
  transition: box-shadow .15s, border-color .15s, transform .12s;
  font-family: inherit;
  display: block; text-decoration: none;
}
.tp-quick-btn:hover {
  box-shadow: 0 4px 16px rgba(0,0,0,.07);
  border-color: #cbd5e1; transform: translateY(-2px);
}
.tp-quick-icon { font-size: 26px; margin-bottom: 10px; }
.tp-quick-label { font-size: 14px; font-weight: 700; color: #0f172a; }
.tp-quick-meta { font-size: 12px; color: #94a3b8; margin-top: 3px; font-weight: 500; }

/* ── Section heading ── */
.tp-section-head { display: flex; align-items: center; gap: 12px; margin: 0 0 16px; }
.tp-section-label { font-size: 11px; font-weight: 700; letter-spacing: .10em; color: #6b7280; text-transform: uppercase; }
.tp-divider { flex: 1; height: 1px; background: #e9ecef; }

/* ── Rent table ── */
.tp-table { width: 100%; border-collapse: collapse; font-size: 13px; }
.tp-table th {
  text-align: left; padding: 8px 6px 12px;
  color: #6b7280; font-weight: 700;
  font-size: 11px; text-transform: uppercase; letter-spacing: .06em;
  border-bottom: 2px solid #f1f5f9;
}
.tp-table td { padding: 12px 6px; border-bottom: 1px solid #f1f5f9; }
.tp-table tr:last-child td { border-bottom: none; }
.tp-badge {
  display: inline-block;
  border-radius: 20px; padding: 2px 10px;
  font-size: 11px; font-weight: 700;
}

/* ── Maintenance form ── */
.tp-form-group { margin-bottom: 14px; }
.tp-label { display: block; font-size: 12px; font-weight: 700; color: #374151; margin-bottom: 5px; }
.tp-input, .tp-textarea, .tp-select {
  width: 100%; padding: 10px 13px;
  border: 1.5px solid #d1d5db; border-radius: 9px;
  font-size: 14px; outline: none;
  font-family: inherit;
  transition: border-color 0.15s;
  box-sizing: border-box;
  color: #0f172a;
}
.tp-input:focus, .tp-textarea:focus, .tp-select:focus { border-color: #0d9488; }
.tp-textarea { resize: vertical; }
.tp-submit-btn {
  background: #0d9488; color: #fff;
  border: none; border-radius: 10px;
  padding: 11px 22px; font-size: 14px; font-weight: 700;
  cursor: pointer; font-family: inherit;
  transition: opacity .15s;
}
.tp-submit-btn:hover { opacity: .88; }
.tp-submit-btn:disabled { background: #9ca3af; cursor: not-allowed; }

/* ── Maintenance item ── */
.tp-mx-item {
  background: #fff; border-radius: 14px;
  border: 1.5px solid #eaeef2; padding: 18px 20px;
  margin-bottom: 10px;
}
.tp-mx-header { display: flex; gap: 14px; align-items: flex-start; }
.tp-mx-dot {
  width: 10px; height: 10px; border-radius: 50%;
  flex-shrink: 0; margin-top: 5px;
}
.tp-mx-title { font-size: 14px; font-weight: 700; color: #0f172a; margin-bottom: 4px; }
.tp-mx-desc { font-size: 13px; color: #64748b; margin-bottom: 6px; }
.tp-mx-meta { font-size: 11px; color: #94a3b8; font-weight: 500; }
.tp-mx-note {
  background: #f0fdfa; border: 1px solid #99f6e4; border-radius: 9px;
  padding: 10px 13px; margin-top: 12px;
  font-size: 13px; color: #065f46;
  display: flex; gap: 8px; align-items: flex-start;
}

/* ── Document item ── */
.tp-doc {
  display: flex; align-items: center; justify-content: space-between;
  background: #f8fafc; border-radius: 12px; padding: 16px 18px;
  margin-bottom: 10px; border: 1.5px solid #eaeef2;
  gap: 12px;
}
.tp-doc-type-chip {
  display: inline-block; background: #dbeafe; color: #1d4ed8;
  border-radius: 6px; padding: 2px 9px;
  font-size: 10px; font-weight: 700; margin-bottom: 4px;
  text-transform: uppercase; letter-spacing: .05em;
}
.tp-doc-download {
  background: #0d9488; color: #fff; text-decoration: none;
  border-radius: 8px; padding: 7px 16px; font-size: 12px; font-weight: 700;
  white-space: nowrap; flex-shrink: 0;
}

/* ── Messages ── */
.tp-success {
  background: #d1fae5; border: 1px solid #6ee7b7;
  border-radius: 9px; padding: 10px 14px;
  font-size: 13px; color: #065f46; margin-bottom: 14px;
}
.tp-error-msg {
  background: #fef2f2; border: 1px solid #fca5a5;
  border-radius: 9px; padding: 10px 14px;
  font-size: 13px; color: #dc2626; margin-bottom: 14px;
}

/* ── Empty states ── */
.tp-empty { text-align: center; padding: 44px 20px; }
.tp-empty-icon { font-size: 40px; margin-bottom: 12px; }
.tp-empty-text { font-size: 15px; font-weight: 800; color: #374151; }
.tp-empty-sub { font-size: 13px; color: #94a3b8; margin-top: 6px; line-height: 1.5; }
.tp-empty-action {
  display: inline-block; margin-top: 16px;
  background: #0d9488; color: #fff; border: none; border-radius: 9px;
  padding: 9px 20px; font-size: 13px; font-weight: 700;
  cursor: pointer; font-family: inherit;
}

/* ── Tab skeleton ── */
.tp-skeleton {
  background: linear-gradient(90deg, #f1f5f9 25%, #e2e8f0 50%, #f1f5f9 75%);
  background-size: 200% 100%;
  animation: tp-shimmer 1.4s infinite;
  border-radius: 10px;
  height: 80px; margin-bottom: 10px;
}
@keyframes tp-shimmer { 0% { background-position: 200% 0; } 100% { background-position: -200% 0; } }

@media (max-width: 640px) {
  .tp-banner { flex-direction: column; align-items: flex-start; gap: 14px; padding: 22px 20px 28px; }
  .tp-banner-right { align-self: stretch; justify-content: space-between; }
  .tp-kpi-row { grid-template-columns: repeat(2, 1fr); }
  .tp-grid-2 { grid-template-columns: 1fr; }
  .tp-grid-3 { grid-template-columns: 1fr 1fr; }
  .tp-mod-grid { grid-template-columns: repeat(2, 1fr); gap: 10px; }
  .tp-tabs-desktop { display: none; }
  .tp-bottom-nav { display: flex; }
  .tp-content { padding: 16px 14px 88px; }
  .tp-doc { flex-wrap: wrap; }
  .tp-table-wrap { overflow-x: auto; -webkit-overflow-scrolling: touch; }
}

/* ── Module tiles (dashboard nav) ── */
.tp-mod-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 12px;
  margin-bottom: 22px;
}
.tp-mod-tile {
  border: none; border-radius: 18px;
  padding: 18px 14px 16px;
  text-align: left; cursor: pointer;
  font-family: inherit;
  color: #fff;
  transition: transform .12s, box-shadow .15s;
  box-shadow: 0 6px 0 rgba(0,0,0,0.12), 0 12px 24px rgba(0,0,0,0.12);
  position: relative;
}
.tp-mod-tile:hover { transform: translateY(-2px); }
.tp-mod-tile.active { outline: 3px solid rgba(255,255,255,0.55); outline-offset: 2px; }
.tp-mod-icon { font-size: 28px; display: block; margin-bottom: 8px; }
.tp-mod-label { font-size: 14px; font-weight: 800; display: block; }
.tp-mod-sub { font-size: 11px; opacity: 0.85; margin-top: 4px; font-weight: 600; }
.tp-mod-badge {
  position: absolute; top: 10px; right: 10px;
  background: rgba(255,255,255,0.95); color: #dc2626;
  border-radius: 10px; padding: 2px 7px;
  font-size: 10px; font-weight: 800;
}

/* ── Bottom nav (mobile) ── */
.tp-bottom-nav {
  display: none;
  position: fixed; bottom: 0; left: 0; right: 0;
  background: #fff; border-top: 1.5px solid #eaeef2;
  padding: 6px 8px calc(6px + env(safe-area-inset-bottom));
  z-index: 50; box-shadow: 0 -4px 20px rgba(0,0,0,0.08);
  justify-content: space-around; gap: 4px;
}
.tp-bnav-btn {
  flex: 1; background: none; border: none;
  padding: 8px 4px; cursor: pointer;
  font-family: inherit; font-size: 10px; font-weight: 700;
  color: #94a3b8; display: flex; flex-direction: column;
  align-items: center; gap: 3px; border-radius: 10px;
}
.tp-bnav-btn.active { color: #0d9488; background: #f0fdfa; }
.tp-bnav-icon { font-size: 20px; }

.tp-photo-preview {
  margin-top: 8px; max-width: 120px; border-radius: 10px;
  border: 1.5px solid #99f6e4;
}
.tp-file-label {
  display: inline-flex; align-items: center; gap: 8px;
  cursor: pointer; background: #f0fdfa;
  border: 1.5px dashed #0d9488; border-radius: 9px;
  padding: 10px 16px; font-size: 13px; color: #0d9488; font-weight: 700;
}
`;

function statusBadge(status: string) {
  const map: Record<string, { bg: string; color: string; label: string }> = {
    paid:          { bg: '#d1fae5', color: '#059669', label: '✓ Paid' },
    overdue:       { bg: '#fee2e2', color: '#dc2626', label: '! Overdue' },
    unpaid:        { bg: '#fef3c7', color: '#b45309', label: '○ Due' },
    pending:       { bg: '#fef3c7', color: '#b45309', label: '○ Pending' },
    open:          { bg: '#dbeafe', color: '#1d4ed8', label: '● Open' },
    reported:      { bg: '#dbeafe', color: '#1d4ed8', label: '● Reported' },
    'in-progress': { bg: '#f3e8ff', color: '#7c3aed', label: '↻ In Progress' },
    in_progress:   { bg: '#f3e8ff', color: '#7c3aed', label: '↻ In Progress' },
    completed:     { bg: '#d1fae5', color: '#059669', label: '✓ Done' },
    closed:        { bg: '#f3f4f6', color: '#6b7280', label: '✓ Closed' },
  };
  const s = map[status?.toLowerCase()] || { bg: '#f3f4f6', color: '#6b7280', label: status };
  return <span className="tp-badge" style={{ background: s.bg, color: s.color }}>{s.label}</span>;
}

const DOC_TYPES: Record<string, { label: string; color: string; bg: string }> = {
  'tenancy_agreement': { label: 'Tenancy Agreement', color: '#1d4ed8', bg: '#dbeafe' },
  'gas_safety':        { label: 'Gas Safety Certificate', color: '#b45309', bg: '#fef3c7' },
  'epc':               { label: 'Energy Performance (EPC)', color: '#065f46', bg: '#d1fae5' },
  'eicr':              { label: 'Electrical Safety (EICR)', color: '#7c3aed', bg: '#f3e8ff' },
  'how_to_rent':       { label: 'How to Rent Guide', color: '#0369a1', bg: '#e0f2fe' },
  'deposit_cert':      { label: 'Deposit Certificate', color: '#374151', bg: '#f3f4f6' },
  'inventory':         { label: 'Inventory / Check-in', color: '#065f46', bg: '#ecfdf5' },
};

function docTypeChip(type: string | null) {
  const t = DOC_TYPES[type || ''] || { label: 'Tenancy Agreement', color: '#1d4ed8', bg: '#dbeafe' };
  return (
    <span className="tp-doc-type-chip" style={{ background: t.bg, color: t.color }}>
      {t.label}
    </span>
  );
}

const certDocKey = (certType: string) => {
  const t = (certType || '').toLowerCase();
  if (t.includes('gas')) return 'gas_safety';
  if (t.includes('epc') || t.includes('energy')) return 'epc';
  if (t.includes('eicr') || t.includes('electrical')) return 'eicr';
  return null;
};

const MX_CATEGORIES = [
  { value: '', label: '— Select issue type —', title: '', priority: 'medium' },
  { value: 'leak', label: '💧 Leak / water damage', title: 'Leak or water damage', priority: 'high' },
  { value: 'heating', label: '🔥 Heating / boiler', title: 'Heating or boiler problem', priority: 'high' },
  { value: 'electrics', label: '⚡ Electrics / power', title: 'Electrical issue', priority: 'high' },
  { value: 'kitchen', label: '🍳 Kitchen', title: 'Kitchen issue', priority: 'medium' },
  { value: 'bathroom', label: '🚿 Bathroom', title: 'Bathroom issue', priority: 'medium' },
  { value: 'walls', label: '🧱 Walls / ceiling', title: 'Wall or ceiling damage', priority: 'medium' },
  { value: 'doors', label: '🚪 Doors / windows', title: 'Door or window issue', priority: 'low' },
  { value: 'pest', label: '🐭 Pest problem', title: 'Pest report', priority: 'medium' },
  { value: 'urgent', label: '🚨 Urgent / safety', title: 'Urgent safety issue', priority: 'high' },
  { value: 'other', label: '📝 Other', title: 'Maintenance request', priority: 'medium' },
] as const;

export default function TenantPortal() {
  const navigate = useNavigate();
  const [tab, setTab] = useState<Tab>('home');

  // Core data from /dashboard (loaded once on mount)
  const [me, setMe] = useState<any>(null);
  const [rent, setRent] = useState<any[]>([]);
  const [maintenance, setMaintenance] = useState<any[]>([]);
  const [documents, setDocuments] = useState<any[]>([]);
  const [sharedCerts, setSharedCerts] = useState<any[]>([]);
  const [paymentDetails, setPaymentDetails] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Per-tab lazy load tracking
  const [tabLoaded, setTabLoaded] = useState<Partial<Record<Tab, boolean>>>({ home: false });
  const [tabLoading, setTabLoading] = useState<Partial<Record<Tab, boolean>>>({});

  // Maintenance form state
  const [showMxForm, setShowMxForm] = useState(false);
  const [mxCategory, setMxCategory] = useState('');
  const [mxTitle, setMxTitle] = useState('');
  const [mxDesc, setMxDesc] = useState('');
  const [mxPriority, setMxPriority] = useState('medium');
  const [mxPhoto, setMxPhoto] = useState<File | null>(null);
  const [mxPhotoPreview, setMxPhotoPreview] = useState('');
  const [mxSubmitting, setMxSubmitting] = useState(false);
  const [mxError, setMxError] = useState('');
  const [mxSuccess, setMxSuccess] = useState('');

  const resetMxForm = () => {
    setMxCategory(''); setMxTitle(''); setMxDesc(''); setMxPriority('medium');
    setMxPhoto(null); setMxPhotoPreview('');
  };

  const onMxCategoryChange = (val: string) => {
    setMxCategory(val);
    const cat = MX_CATEGORIES.find(c => c.value === val);
    if (cat?.value) {
      setMxTitle(cat.title);
      setMxPriority(cat.priority);
    }
  };

  const token = localStorage.getItem('tenantToken');
  const authHeaders = { Authorization: `Bearer ${token}` };

  // Initial load: fetch all from optimised /dashboard endpoint
  const fetchDashboard = useCallback(async () => {
    if (!token) { navigate('/tenant/login'); return; }
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${API_URL}/tenant-portal/dashboard`, { headers: authHeaders });
      if (res.status === 401) {
        localStorage.removeItem('tenantToken');
        navigate('/tenant/login');
        return;
      }
      const data = await res.json();
      if (!res.ok) { setError(data.error || 'Failed to load'); return; }
      setMe({ tenant: data.tenant, lease: data.lease, landlord: data.landlord });
      setRent(data.rent || []);
      setMaintenance(data.maintenance || []);
      setDocuments(data.documents || []);
      setSharedCerts(data.certificates || []);
      setPaymentDetails(data.payment_details || null);
      setTabLoaded({ home: true, rent: true, maintenance: true, documents: true });
    } catch {
      setError('Failed to load portal. Please refresh.');
    } finally {
      setLoading(false);
    }
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { fetchDashboard(); }, [fetchDashboard]);

  // Lazy refresh for individual tabs (if user explicitly navigates)
  const refreshTab = useCallback(async (t: Tab) => {
    if (tabLoaded[t]) return; // already loaded
    setTabLoading(p => ({ ...p, [t]: true }));
    try {
      if (t === 'rent') {
        const res = await fetch(`${API_URL}/tenant-portal/rent`, { headers: authHeaders });
        if (res.ok) {
          const data = await res.json();
          setRent(data.rent || data);
          if (data.payment_details) setPaymentDetails(data.payment_details);
        }
      } else if (t === 'maintenance') {
        const res = await fetch(`${API_URL}/tenant-portal/maintenance`, { headers: authHeaders });
        if (res.ok) setMaintenance(await res.json());
      } else if (t === 'documents') {
        const [docsRes, certsRes] = await Promise.all([
          fetch(`${API_URL}/tenant-portal/documents`, { headers: authHeaders }),
          fetch(`${API_URL}/tenant-portal/certificates`, { headers: authHeaders }),
        ]);
        if (docsRes.ok) setDocuments(await docsRes.json());
        if (certsRes.ok) {
          const cd = await certsRes.json();
          setSharedCerts(cd.certificates || []);
        }
      }
      setTabLoaded(p => ({ ...p, [t]: true }));
    } finally {
      setTabLoading(p => ({ ...p, [t]: false }));
    }
  }, [tabLoaded]); // eslint-disable-line react-hooks/exhaustive-deps

  const switchTab = (t: Tab) => {
    setTab(t);
    refreshTab(t);
  };

  const logout = () => {
    localStorage.removeItem('tenantToken');
    localStorage.removeItem('tenantInfo');
    navigate('/tenant/login');
  };

  const submitMaintenance = async (e: React.FormEvent) => {
    e.preventDefault();
    setMxError('');
    setMxSuccess('');
    if (!mxCategory) { setMxError('Please select an issue type'); return; }
    if (!mxDesc.trim()) { setMxError('Please describe the problem'); return; }
    setMxSubmitting(true);
    try {
      const fd = new FormData();
      fd.append('category', mxCategory);
      fd.append('title', mxTitle || MX_CATEGORIES.find(c => c.value === mxCategory)?.title || 'Maintenance request');
      fd.append('description', mxDesc.trim());
      fd.append('priority', mxPriority);
      if (mxPhoto) fd.append('photo', mxPhoto);
      const res = await fetch(`${API_URL}/tenant-portal/maintenance`, {
        method: 'POST',
        headers: authHeaders,
        body: fd,
      });
      const data = await res.json();
      if (!res.ok) { setMxError(data.error || 'Failed to submit'); return; }
      setMxSuccess('Request submitted! Your landlord has been notified.');
      resetMxForm();
      setShowMxForm(false);
      setMaintenance(prev => [data, ...prev]);
    } catch {
      setMxError('Network error. Please try again.');
    } finally {
      setMxSubmitting(false);
    }
  };

  /* ── Computed values ── */
  const lease    = me?.lease;
  const tenant   = me?.tenant;
  const landlord = me?.landlord;

  const daysLeft = lease?.end_date
    ? Math.round((new Date(lease.end_date).getTime() - Date.now()) / 86400000)
    : null;

  const thisMonth     = new Date().toISOString().slice(0, 7);
  const paidThisMonth = rent.some(r => r.status === 'paid' && r.month === thisMonth);
  const arrears       = rent.filter(r =>
    r.status === 'overdue' || (r.status === 'unpaid' && r.due_date && new Date(r.due_date) < new Date())
  );
  const totalArrears = arrears.reduce((s: number, r: any) => s + Number(r.amount_due || 0), 0);
  const openMaint    = maintenance.filter(m => m.status === 'open' || m.status === 'reported').length;
  const nextDue      = nextDueDate(rent, lease);

  /* ── Tab config ── */
  const navTabs: { id: Tab; icon: string; label: string; badge?: number }[] = [
    { id: 'home',        icon: '🏠', label: 'My Home' },
    { id: 'rent',        icon: '💷', label: 'Rent' },
    { id: 'maintenance', icon: '🔧', label: 'Maintenance', badge: openMaint || undefined },
    { id: 'documents',   icon: '📄', label: 'Documents' },
  ];

  const moduleTiles = [
    { id: 'home' as Tab, icon: '🏠', label: 'My Home', sub: 'Property & tenancy', bg: 'linear-gradient(145deg,#064e3b,#0d9488,#14b8a6)' },
    { id: 'rent' as Tab, icon: '💷', label: 'Rent', sub: paidThisMonth ? 'Paid this month' : totalArrears > 0 ? `${fmt(totalArrears)} overdue` : `${rent.length} records`, bg: 'linear-gradient(145deg,#0369a1,#0284c7,#0ea5e9)' },
    { id: 'maintenance' as Tab, icon: '🔧', label: 'Maintenance', sub: openMaint > 0 ? `${openMaint} open` : 'Report an issue', bg: 'linear-gradient(145deg,#c2410c,#ea580c,#f97316)', badge: openMaint },
    { id: 'documents' as Tab, icon: '📄', label: 'Documents', sub: `${documents.length + sharedCerts.length} on file`, bg: 'linear-gradient(145deg,#4338ca,#6366f1,#818cf8)' },
  ];

  /* ── KPI config ── */
  const rentStatus = totalArrears > 0 ? 'overdue' : paidThisMonth ? 'paid' : 'due';
  const kpiCards = [
    {
      icon: '💷', label: 'Monthly Rent',
      val: fmt(lease?.monthly_rent || 0), sub: 'per month',
      bg: 'linear-gradient(135deg,#0e7490,#0891b2,#06b6d4)',
      shadow: 'rgba(8,145,178,0.4)',
    },
    {
      icon: rentStatus === 'paid' ? '✅' : rentStatus === 'overdue' ? '⚠️' : '🕐',
      label: 'Rent This Month',
      val: rentStatus === 'paid' ? 'Paid' : rentStatus === 'overdue' ? fmt(totalArrears) : 'Due',
      sub: rentStatus === 'paid' ? 'All up to date' : rentStatus === 'overdue' ? 'Contact landlord' : 'Payment due',
      bg: rentStatus === 'paid'
        ? 'linear-gradient(135deg,#166534,#16a34a,#22c55e)'
        : rentStatus === 'overdue'
          ? 'linear-gradient(135deg,#991b1b,#dc2626,#ef4444)'
          : 'linear-gradient(135deg,#92400e,#d97706,#fbbf24)',
      shadow: rentStatus === 'paid' ? 'rgba(22,163,74,0.4)' : rentStatus === 'overdue' ? 'rgba(220,38,38,0.4)' : 'rgba(217,119,6,0.4)',
    },
    {
      icon: '📅',
      label: 'Next Rent Due',
      val: nextDue ? fmtDate(nextDue) : '—',
      sub: lease?.payment_day ? `Day ${lease.payment_day} each month` : 'Check with landlord',
      bg: paidThisMonth
        ? 'linear-gradient(135deg,#1e293b,#334155,#475569)'
        : 'linear-gradient(135deg,#78350f,#d97706,#f59e0b)',
      shadow: paidThisMonth ? 'rgba(30,41,59,0.3)' : 'rgba(217,119,6,0.4)',
    },
    {
      icon: daysLeft !== null && daysLeft <= 60 ? '📋' : openMaint > 0 ? '🔧' : '✅',
      label: daysLeft !== null && daysLeft <= 60 ? 'Lease Expires' : 'Open Issues',
      val: daysLeft !== null && daysLeft <= 60 ? `${daysLeft}d` : openMaint > 0 ? String(openMaint) : '—',
      sub: daysLeft !== null && daysLeft <= 60 ? fmtDate(lease?.end_date) : openMaint > 0 ? 'Awaiting resolution' : 'No open issues',
      bg: daysLeft !== null && daysLeft <= 60
        ? 'linear-gradient(135deg,#78350f,#d97706,#f59e0b)'
        : openMaint > 0
          ? 'linear-gradient(135deg,#c2410c,#ea580c,#f97316)'
          : 'linear-gradient(135deg,#1e293b,#334155,#475569)',
      shadow: daysLeft !== null && daysLeft <= 60 ? 'rgba(217,119,6,0.4)' : openMaint > 0 ? 'rgba(234,88,12,0.4)' : 'rgba(30,41,59,0.3)',
    },
  ];

  /* ── Loading ── */
  if (loading) return (
    <div className="tp-wrap" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 12 }}>
      <style>{CSS}</style>
      <div style={{ fontSize: 40 }}>🏠</div>
      <p style={{ color: '#6b7280' }}>Loading your portal…</p>
    </div>
  );

  if (error) return (
    <div className="tp-wrap" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <style>{CSS}</style>
      <div style={{ textAlign: 'center' }}>
        <p style={{ color: '#dc2626' }}>{error}</p>
        <button onClick={fetchDashboard} style={{ background: '#0d9488', color: '#fff', border: 'none', borderRadius: 8, padding: '9px 18px', fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>Retry</button>
      </div>
    </div>
  );

  return (
    <div className="tp-wrap">
      <style>{CSS}</style>

      {/* ── 3D Banner ── */}
      <div className="tp-banner">
        <div className="tp-banner-left">
          <div className="tp-banner-icon">🏠</div>
          <div>
            <p className="tp-banner-greeting">{greeting()}</p>
            <h1 className="tp-banner-name">{tenant?.full_name || 'Tenant Portal'}</h1>
            {tenant?.property_address && (
              <p className="tp-banner-address">
                <span>📍</span>
                {tenant.property_address}{tenant?.room_number ? ` · Room ${tenant.room_number}` : ''}
              </p>
            )}
          </div>
        </div>
        <div className="tp-banner-right">
          <div className="tp-banner-date">
            {new Date().toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
          </div>
          <button className="tp-logout-btn" onClick={logout}>Sign Out</button>
        </div>
      </div>

      {/* ── Tab nav (desktop) ── */}
      <div className="tp-tabs tp-tabs-desktop">
        {navTabs.map(t => (
          <button
            key={t.id}
            className={`tp-tab${tab === t.id ? ' active' : ''}`}
            onClick={() => switchTab(t.id)}
          >
            {t.icon} {t.label}
            {t.badge ? <span className="tp-tab-badge">{t.badge}</span> : null}
          </button>
        ))}
      </div>

      <div className="tp-content">

        {/* ── Module dashboard tiles (home hub) ── */}
        {tab === 'home' && (
        <div className="tp-mod-grid">
          {moduleTiles.map(m => (
            <button
              key={m.id}
              type="button"
              className="tp-mod-tile"
              style={{ background: m.bg }}
              onClick={() => {
                if (m.id === 'maintenance') setShowMxForm(false);
                switchTab(m.id);
              }}
            >
              {m.badge ? <span className="tp-mod-badge">{m.badge}</span> : null}
              <span className="tp-mod-icon">{m.icon}</span>
              <span className="tp-mod-label">{m.label}</span>
              <span className="tp-mod-sub">{m.sub}</span>
            </button>
          ))}
        </div>
        )}

        {/* ── KPI row (home only) ── */}
        {tab === 'home' && (
        <div className="tp-kpi-row">
          {kpiCards.map((k, i) => (
            <div key={i} className="tp-kpi" style={{
              background: k.bg,
              boxShadow: `0 6px 0 ${k.shadow}, 0 14px 28px ${k.shadow.replace('0.4', '0.18')}`,
            }}>
              <span className="tp-kpi-icon">{k.icon}</span>
              <div className="tp-kpi-label">{k.label}</div>
              <div className="tp-kpi-val">{k.val}</div>
              <div className="tp-kpi-sub">{k.sub}</div>
            </div>
          ))}
        </div>
        )}

        {/* ── HOME TAB ── */}
        {tab === 'home' && (
          <>
            {/* Alert banners */}
            {totalArrears > 0 && (
              <div className="tp-alert" style={{ background: '#fee2e2', borderColor: '#fca5a5' }}>
                <span className="tp-alert-icon">⚠️</span>
                <div>
                  <div className="tp-alert-title" style={{ color: '#dc2626' }}>Rent overdue — {fmt(totalArrears)} outstanding</div>
                  <div className="tp-alert-sub" style={{ color: '#7f1d1d' }}>Please contact your landlord to arrange payment as soon as possible. This may affect your tenancy.</div>
                </div>
              </div>
            )}
            {paidThisMonth && totalArrears === 0 && (
              <div className="tp-alert" style={{ background: '#d1fae5', borderColor: '#6ee7b7' }}>
                <span className="tp-alert-icon">✅</span>
                <div className="tp-alert-title" style={{ color: '#065f46' }}>Rent paid this month — you're all up to date</div>
              </div>
            )}
            {!paidThisMonth && totalArrears === 0 && nextDue && (
              <div className="tp-alert" style={{ background: '#fef9c3', borderColor: '#fde047' }}>
                <span className="tp-alert-icon">🕐</span>
                <div>
                  <div className="tp-alert-title" style={{ color: '#713f12' }}>Rent due {fmtDate(nextDue)}</div>
                  <div className="tp-alert-sub" style={{ color: '#854d0e' }}>Your rent of {fmt(lease?.monthly_rent)} is due. Contact your landlord if you have any questions about payment.</div>
                </div>
              </div>
            )}
            {daysLeft !== null && daysLeft <= 60 && daysLeft >= 0 && (
              <div className="tp-alert" style={{ background: '#fef3c7', borderColor: '#fcd34d' }}>
                <span className="tp-alert-icon">📅</span>
                <div>
                  <div className="tp-alert-title" style={{ color: '#92400e' }}>Tenancy expires in {daysLeft} days</div>
                  <div className="tp-alert-sub" style={{ color: '#78350f' }}>Your tenancy ends on {fmtDate(lease.end_date)}. Contact your landlord to discuss renewal or your move-out plan.</div>
                </div>
              </div>
            )}

            {/* My Property */}
            <div className="tp-card">
              <h3 className="tp-card-title">🏠 My Property</h3>
              <div className="tp-grid-2">
                <div>
                  <div className="tp-field-label">Address</div>
                  <div className="tp-field-val">{tenant?.property_address || '—'}</div>
                </div>
                {tenant?.room_number && (
                  <div>
                    <div className="tp-field-label">Room / Unit</div>
                    <div className="tp-field-val">{tenant.room_number}</div>
                  </div>
                )}
                <div>
                  <div className="tp-field-label">Move-in Date</div>
                  <div className="tp-field-val">{fmtDate(tenant?.move_in_date)}</div>
                </div>
                {landlord?.name && (
                  <div>
                    <div className="tp-field-label">Landlord</div>
                    <div className="tp-field-val">{landlord.name}</div>
                  </div>
                )}
                {landlord?.email && (
                  <div>
                    <div className="tp-field-label">Contact Email</div>
                    <a href={`mailto:${landlord.email}`} className="tp-field-val" style={{ color: '#0d9488', textDecoration: 'none' }}>{landlord.email}</a>
                  </div>
                )}
                {landlord?.phone && (
                  <div>
                    <div className="tp-field-label">Contact Phone</div>
                    <a href={`tel:${landlord.phone}`} className="tp-field-val" style={{ color: '#0d9488', textDecoration: 'none' }}>{landlord.phone}</a>
                  </div>
                )}
              </div>
            </div>

            {/* Tenancy Details */}
            {lease && (
              <div className="tp-card">
                <h3 className="tp-card-title">📋 My Tenancy</h3>
                <div className="tp-grid-3">
                  <div>
                    <div className="tp-field-label">Monthly Rent</div>
                    <div style={{ fontSize: 22, fontWeight: 800, color: '#0d9488', fontFamily: 'DM Mono, monospace' }}>{fmt(lease.monthly_rent)}</div>
                  </div>
                  <div>
                    <div className="tp-field-label">Lease Start</div>
                    <div className="tp-field-val">{fmtDate(lease.start_date)}</div>
                  </div>
                  <div>
                    <div className="tp-field-label">
                      Lease End {daysLeft !== null && daysLeft >= 0 && (
                        <span style={{ color: daysLeft <= 60 ? '#f59e0b' : '#10b981', marginLeft: 4 }}>({daysLeft}d)</span>
                      )}
                    </div>
                    <div className="tp-field-val">{fmtDate(lease.end_date)}</div>
                  </div>
                  <div>
                    <div className="tp-field-label">Deposit</div>
                    <div className="tp-field-val">{fmt(lease.deposit)}</div>
                  </div>
                  <div>
                    <div className="tp-field-label">Notice Period</div>
                    <div className="tp-field-val">{lease.notice_period || 1} month{(lease.notice_period || 1) !== 1 ? 's' : ''}</div>
                  </div>
                  {lease.deposit_scheme && (
                    <div>
                      <div className="tp-field-label">Deposit Protected</div>
                      <div className="tp-field-val" style={{ color: '#059669' }}>✓ {lease.deposit_scheme}</div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {landlord?.email && (
              <div className="tp-card" style={{ background: '#f0fdfa', borderColor: '#99f6e4' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 800, color: '#065f46' }}>Need to speak to your landlord?</div>
                    <div style={{ fontSize: 12, color: '#047857', marginTop: 4 }}>{landlord.email}{landlord.phone ? ` · ${landlord.phone}` : ''}</div>
                  </div>
                  <a href={`mailto:${landlord.email}`} className="tp-submit-btn" style={{ textDecoration: 'none', display: 'inline-block' }}>✉️ Email</a>
                </div>
              </div>
            )}
          </>
        )}

        {/* ── RENT TAB ── */}
        {tab === 'rent' && (
          <>
            {paymentDetails && (
              <div className="tp-card" style={{ marginBottom: 16, background: paymentDetails.complete ? '#eff6ff' : '#fffbeb', borderColor: paymentDetails.complete ? '#bfdbfe' : '#fde68a' }}>
                <h3 className="tp-card-title" style={{ marginBottom: 12 }}>🏦 Pay by Bank Transfer</h3>
                {paymentDetails.complete ? (
                  <>
                    <p style={{ margin: '0 0 14px', fontSize: 13, color: '#1e40af', lineHeight: 1.6 }}>
                      Transfer rent to your landlord using the details below.
                      {paymentDetails.amount ? ` Amount due: ${fmt(paymentDetails.amount)}.` : ''}
                    </p>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 12 }}>
                      {[
                        { label: 'Account name', value: paymentDetails.account_name },
                        { label: 'Bank', value: paymentDetails.bank_name },
                        { label: 'Sort code', value: paymentDetails.sort_code },
                        { label: 'Account number', value: paymentDetails.account_number },
                      ].map(row => (
                        <div key={row.label} style={{ background: '#fff', borderRadius: 10, padding: '12px 14px', border: '1px solid #dbeafe' }}>
                          <div style={{ fontSize: 10, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '.06em', color: '#64748b', marginBottom: 4 }}>{row.label}</div>
                          <div style={{ fontSize: 14, fontWeight: 800, color: '#0f172a', fontFamily: 'DM Mono, monospace', wordBreak: 'break-all' }}>{row.value}</div>
                        </div>
                      ))}
                    </div>
                    {paymentDetails.reference && (
                      <div style={{ marginTop: 14, padding: '10px 14px', background: '#fef3c7', borderRadius: 8, fontSize: 13, color: '#92400e' }}>
                        <strong>Payment reference:</strong> {paymentDetails.reference}
                      </div>
                    )}
                    <button
                      type="button"
                      onClick={() => {
                        const lines = [
                          `Account name: ${paymentDetails.account_name}`,
                          `Sort code: ${paymentDetails.sort_code}`,
                          `Account number: ${paymentDetails.account_number}`,
                          paymentDetails.reference ? `Reference: ${paymentDetails.reference}` : '',
                          paymentDetails.amount ? `Amount: ${fmt(paymentDetails.amount)}` : '',
                        ].filter(Boolean).join('\n');
                        navigator.clipboard.writeText(lines).catch(() => undefined);
                      }}
                      style={{ marginTop: 14, padding: '8px 16px', borderRadius: 8, border: '1px solid #93c5fd', background: '#fff', color: '#1d4ed8', fontWeight: 700, fontSize: 13, cursor: 'pointer' }}
                    >
                      📋 Copy bank details
                    </button>
                  </>
                ) : (
                  <p style={{ margin: 0, fontSize: 13, color: '#92400e', lineHeight: 1.6 }}>
                    {paymentDetails.message || 'Contact your landlord for payment instructions.'}
                    {landlord?.email && (
                      <> <a href={`mailto:${landlord.email}`} style={{ color: '#b45309', fontWeight: 700 }}>Email landlord</a></>
                    )}
                  </p>
                )}
              </div>
            )}

          <div className="tp-card">
            <h3 className="tp-card-title">💷 Rent Payment History</h3>
            {tabLoading.rent ? (
              <><div className="tp-skeleton" /><div className="tp-skeleton" /></>
            ) : rent.length === 0 ? (
              <div className="tp-empty">
                <div className="tp-empty-icon">💷</div>
                <div className="tp-empty-text">No rent records yet</div>
                <div className="tp-empty-sub">
                  Your landlord hasn't added rent records yet.<br />
                  Contact them if you need a payment receipt.
                </div>
                {landlord?.email && (
                  <a href={`mailto:${landlord.email}`} className="tp-empty-action" style={{ color: '#fff', textDecoration: 'none' }}>
                    ✉️ Email Landlord
                  </a>
                )}
              </div>
            ) : (
              <>
                {totalArrears > 0 && (
                  <div style={{ background: '#fee2e2', borderRadius: 10, padding: '10px 14px', marginBottom: 16, fontSize: 13, color: '#dc2626', fontWeight: 700 }}>
                    ⚠️ {fmt(totalArrears)} overdue — please contact your landlord
                  </div>
                )}
                <div className="tp-table-wrap">
                <table className="tp-table">
                  <thead>
                    <tr>
                      <th>Month</th>
                      <th>Due Date</th>
                      <th>Amount</th>
                      <th>Status</th>
                      <th>Paid Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rent.map((r: any, i: number) => (
                      <tr key={i} style={{ background: r.status === 'overdue' ? '#fff5f5' : 'transparent' }}>
                        <td style={{ fontWeight: 700 }}>{r.month}</td>
                        <td style={{ color: '#64748b' }}>{fmtDate(r.due_date)}</td>
                        <td style={{ fontFamily: 'DM Mono, monospace', fontWeight: 700 }}>{fmt(r.amount_due)}</td>
                        <td>{statusBadge(r.status)}</td>
                        <td style={{ color: '#64748b' }}>{r.paid_date ? fmtDate(r.paid_date) : '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                </div>
              </>
            )}
          </div>
          </>
        )}

        {/* ── MAINTENANCE TAB ── */}
        {tab === 'maintenance' && (
          <>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <h3 style={{ margin: 0, fontSize: 17, fontWeight: 800, color: '#0f172a' }}>🔧 Maintenance Requests</h3>
              <button
                onClick={() => { setShowMxForm(v => !v); setMxError(''); setMxSuccess(''); }}
                style={{
                  background: showMxForm ? '#f1f5f9' : '#0d9488',
                  color: showMxForm ? '#64748b' : '#fff',
                  border: 'none', borderRadius: 9, padding: '9px 16px',
                  fontSize: 13, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit',
                }}
              >
                {showMxForm ? '✖ Cancel' : '🛠️ Report'}
              </button>
            </div>

            {mxSuccess && <div className="tp-success">✅ {mxSuccess}</div>}

            {showMxForm && (
              <div className="tp-card" style={{ borderColor: '#0d9488', background: '#f0fdfa', marginBottom: 16 }}>
                <h4 style={{ margin: '0 0 16px', color: '#0d9488', fontWeight: 800, fontSize: 15 }}>Report a New Issue</h4>
                {mxError && <div className="tp-error-msg">{mxError}</div>}
                <form onSubmit={submitMaintenance}>
                  <div className="tp-form-group">
                    <label className="tp-label">Issue Type *</label>
                    <select className="tp-select" value={mxCategory} onChange={e => onMxCategoryChange(e.target.value)} required>
                      {MX_CATEGORIES.map(c => <option key={c.value || 'none'} value={c.value}>{c.label}</option>)}
                    </select>
                  </div>
                  <div className="tp-form-group">
                    <label className="tp-label">Short Title</label>
                    <input className="tp-input" value={mxTitle} onChange={e => setMxTitle(e.target.value)} placeholder="Auto-filled from issue type" />
                  </div>
                  <div className="tp-form-group">
                    <label className="tp-label">What is the problem? *</label>
                    <textarea className="tp-textarea" value={mxDesc} onChange={e => setMxDesc(e.target.value)} required rows={4} placeholder="Describe what happened — when it started, which room, any safety concerns…" />
                  </div>
                  <div className="tp-form-group">
                    <label className="tp-label">Urgency</label>
                    <select className="tp-select" value={mxPriority} onChange={e => setMxPriority(e.target.value)}>
                      <option value="low">🟢 Low — minor inconvenience</option>
                      <option value="medium">🟡 Medium — needs attention soon</option>
                      <option value="high">🔴 High — urgent, affecting daily life</option>
                    </select>
                  </div>
                  <div className="tp-form-group">
                    <label className="tp-label">Photo (optional, 1 image — JPG/PNG, max 10MB)</label>
                    <label className="tp-file-label">
                      📷 {mxPhoto ? mxPhoto.name : 'Add photo…'}
                      <input type="file" accept="image/jpeg,image/png" style={{ display: 'none' }}
                        onChange={e => {
                          const f = e.target.files?.[0];
                          if (!f) return;
                          if (f.size > 10485760) { setMxError('Photo must be under 10MB'); return; }
                          setMxPhoto(f);
                          setMxPhotoPreview(URL.createObjectURL(f));
                          setMxError('');
                        }} />
                    </label>
                    {mxPhotoPreview && <img src={mxPhotoPreview} alt="Preview" className="tp-photo-preview" />}
                  </div>
                  <button type="submit" className="tp-submit-btn" disabled={mxSubmitting}>
                    {mxSubmitting ? 'Sending…' : '📤 Send'}
                  </button>
                </form>
              </div>
            )}

            {tabLoading.maintenance ? (
              <><div className="tp-skeleton" /><div className="tp-skeleton" /></>
            ) : maintenance.length === 0 && !showMxForm ? (
              <div className="tp-card">
                <div className="tp-empty">
                  <div className="tp-empty-icon">🔧</div>
                  <div className="tp-empty-text">No maintenance requests yet</div>
                  <div className="tp-empty-sub">
                    Something broken or not working properly?<br />
                    Use the button above to report it — your landlord will be notified.
                  </div>
                  <button className="tp-empty-action" onClick={() => { setShowMxForm(true); resetMxForm(); }}>
                    🛠️ Report
                  </button>
                </div>
              </div>
            ) : (
              maintenance.map((m: any, i: number) => {
                const dotColor =
                  m.status === 'completed' || m.status === 'closed' ? '#16a34a' :
                  m.status === 'in-progress' ? '#7c3aed' : '#3b82f6';
                return (
                  <div key={i} className="tp-mx-item">
                    <div className="tp-mx-header">
                      <div className="tp-mx-dot" style={{ background: dotColor }} />
                      <div style={{ flex: 1 }}>
                        <div className="tp-mx-title">{m.title || m.description}</div>
                        {m.title && m.description && <div className="tp-mx-desc">{m.description}</div>}
                        <div className="tp-mx-meta">
                          Reported {fmtDate(m.created_at)}
                          {m.updated_at && m.updated_at !== m.created_at && ` · Updated ${fmtDate(m.updated_at)}`}
                        </div>
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 6, flexShrink: 0 }}>
                        {statusBadge(m.status)}
                        {m.priority && (
                          <span style={{ fontSize: 11, color: ['urgent', 'high'].includes(m.priority) ? '#dc2626' : ['routine', 'medium'].includes(m.priority) ? '#d97706' : '#059669' }}>
                            {['urgent', 'high'].includes(m.priority) ? '🔴' : ['routine', 'medium'].includes(m.priority) ? '🟡' : '🟢'}
                            {' '}{m.priority === 'urgent' || m.priority === 'high' ? 'Urgent' : m.priority === 'routine' || m.priority === 'medium' ? 'Routine' : 'Low'}
                          </span>
                        )}
                      </div>
                    </div>
                    {m.landlord_note && (
                      <div className="tp-mx-note">
                        <span style={{ fontSize: 16, flexShrink: 0 }}>💬</span>
                        <div>
                          <div style={{ fontWeight: 700, fontSize: 12, color: '#047857', marginBottom: 2 }}>Note from landlord</div>
                          <div>{m.landlord_note}</div>
                        </div>
                      </div>
                    )}
                    {m.file_url && (
                      <div style={{ marginTop: 12 }}>
                        <a href={m.file_url} target="_blank" rel="noopener noreferrer" style={{ fontSize: 12, fontWeight: 700, color: '#0d9488' }}>📷 View photo</a>
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </>
        )}

        {/* ── DOCUMENTS TAB ── */}
        {tab === 'documents' && (
          <div className="tp-card">
            <h3 className="tp-card-title">📄 My Tenancy Documents</h3>

            {/* UK compliance checklist */}
            <div style={{ background: '#f8fafc', borderRadius: 12, padding: '14px 18px', marginBottom: 20, border: '1.5px solid #eaeef2' }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: 10 }}>
                UK Legal Documents — Your Rights
              </div>
              {[
                { key: 'gas_safety', label: 'Gas Safety Certificate', required: true },
                { key: 'epc', label: 'Energy Performance Certificate (EPC)', required: true },
                { key: 'eicr', label: 'Electrical Safety Certificate (EICR)', required: true },
                { key: 'how_to_rent', label: 'How to Rent Guide (Gov.uk)', required: true },
                { key: 'tenancy_agreement', label: 'Tenancy Agreement', required: true },
                { key: 'deposit_cert', label: 'Deposit Protection Certificate', required: !!lease?.deposit_scheme },
              ].map(item => {
                const have = documents.some(d => d.document_type === item.key || (!d.document_type && item.key === 'tenancy_agreement'))
                  || sharedCerts.some(c => certDocKey(c.cert_type) === item.key);
                return (
                  <div key={item.key} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '5px 0', fontSize: 13, borderBottom: '1px solid #f1f5f9' }}>
                    <span style={{ fontSize: 15 }}>{have ? '✅' : item.required ? '⬜' : '—'}</span>
                    <span style={{ color: have ? '#065f46' : '#374151', fontWeight: have ? 600 : 400 }}>{item.label}</span>
                    {!have && item.required && (
                      <span style={{ marginLeft: 'auto', fontSize: 11, color: '#dc2626', fontWeight: 700 }}>Missing — ask landlord</span>
                    )}
                  </div>
                );
              })}
            </div>

            {tabLoading.documents ? (
              <><div className="tp-skeleton" /><div className="tp-skeleton" /></>
            ) : documents.length === 0 && sharedCerts.length === 0 ? (
              <div className="tp-empty">
                <div className="tp-empty-icon">📄</div>
                <div className="tp-empty-text">No documents uploaded yet</div>
                <div className="tp-empty-sub">
                  You are legally entitled to receive your Gas Safety Certificate, EPC, and How to Rent guide.<br />
                  Contact your landlord to request these.
                </div>
                {landlord?.email && (
                  <a href={`mailto:${landlord.email}?subject=Tenancy%20Documents%20Request&body=Hi%2C%0A%0ACould%20you%20please%20provide%20copies%20of%20my%20Gas%20Safety%20Certificate%2C%20EPC%2C%20EICR%2C%20How%20to%20Rent%20guide%20and%20Tenancy%20Agreement.%0A%0AThank%20you`}
                    className="tp-empty-action" style={{ color: '#fff', textDecoration: 'none' }}>
                    ✉️ Request Documents
                  </a>
                )}
              </div>
            ) : (
              <>
                {sharedCerts.map((cert: any) => (
                  <div key={`cert-${cert.id}`} className="tp-doc">
                    <div style={{ minWidth: 0 }}>
                      {docTypeChip(certDocKey(cert.cert_type))}
                      <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 3 }}>{cert.cert_type}</div>
                      <div style={{ fontSize: 12, color: '#94a3b8' }}>
                        Issued {fmtDate(cert.issue_date)}{cert.expiry_date ? ` · Valid until ${fmtDate(cert.expiry_date)}` : ''}
                      </div>
                    </div>
                    {cert.file_url ? (
                      <a href={cert.file_url} target="_blank" rel="noopener noreferrer" className="tp-doc-download">
                        ⬇ Download
                      </a>
                    ) : null}
                  </div>
                ))}
                {documents.map((doc: any, i: number) => (
                  <div key={i} className="tp-doc">
                    <div style={{ minWidth: 0 }}>
                      {docTypeChip(doc.document_type)}
                      <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 3 }}>
                        {DOC_TYPES[doc.document_type]?.label || 'Tenancy Agreement'}
                      </div>
                      <div style={{ fontSize: 12, color: '#94a3b8' }}>
                        {fmtDate(doc.start_date)} → {fmtDate(doc.end_date)} · {fmt(doc.monthly_rent)}/mo
                      </div>
                    </div>
                    {doc.document_url ? (
                      <a href={doc.document_url} target="_blank" rel="noopener noreferrer" className="tp-doc-download">
                        ⬇ Download
                      </a>
                    ) : (
                      <span style={{ fontSize: 12, color: '#94a3b8', flexShrink: 0 }}>No file attached</span>
                    )}
                  </div>
                ))}
              </>
            )}
          </div>
        )}

      </div>

      {/* ── Mobile bottom nav ── */}
      <nav className="tp-bottom-nav">
        {navTabs.map(t => (
          <button
            key={t.id}
            type="button"
            className={`tp-bnav-btn${tab === t.id ? ' active' : ''}`}
            onClick={() => switchTab(t.id)}
          >
            <span className="tp-bnav-icon">{t.icon}</span>
            {t.label}{t.badge ? ` (${t.badge})` : ''}
          </button>
        ))}
      </nav>
    </div>
  );
}

